import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { useRouter } from '@/routing/navigation'
import { getPlanCard } from '@/pages/SettingsPage/components/billing/planConfig'
import { BillingLockedTooltip } from '@/components/billing/BillingLockedTooltip'
import { useTranslation } from 'react-i18next'
import { ROUTES } from '@/routes/routes'

interface UpgradeModalProps {
  open: boolean
  onClose?: () => void
}

/**
 * Trial-expired modal: shown when a trial-like account
 * (`trial`, `partner_trial`) has either run out of credits or hit its
 * `trial_ends_at` deadline. Presents Enterprise; the upgrade CTA is locked
 * (contact support) while self-serve checkout is disabled.
 *
 * Modal is dismissible: users can close it to browse the platform
 * in view-only mode, navigate to plans, etc.
 */
export function UpgradeModal({ open, onClose }: UpgradeModalProps) {
  const router = useRouter()
  const { t } = useTranslation('app')
  const enterpriseCard = getPlanCard('enterprise')
  const monthlyPriceUsd = enterpriseCard.monthlyPriceUsd
  const isCustomPrice = monthlyPriceUsd === null
  const enterprisePriceLabel = isCustomPrice
    ? t('billing.custom')
    : `$${monthlyPriceUsd.toLocaleString()}`

  const handleViewPlans = () => {
    router.push(ROUTES.billingPlans)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => { if (!isOpen) onClose?.() }}
    >
      <DialogContent
        className="max-w-sm gap-0 overflow-hidden rounded-2xl bg-background p-0 sm:p-0"
      >
        <div className="flex flex-col gap-6 px-8 pt-10 pb-8">
          <DialogHeader className="flex flex-col gap-2 text-center">
            <DialogTitle className="font-display text-2xl font-normal leading-tight tracking-tight text-foreground">
              Your trial has ended
            </DialogTitle>
            <DialogDescription className="text-sm leading-snug text-muted-foreground">
              Upgrade to Enterprise to keep going.
            </DialogDescription>
          </DialogHeader>

          {/* Enterprise plan card */}
          <div
            className="relative flex flex-col items-center gap-4 rounded-2xl border border-[hsl(var(--platform-card))] bg-[hsl(var(--platform-card))]/20 px-5 py-6"
          >
            <Text size="sm" weight="medium" className="text-foreground">
              Enterprise
            </Text>

            <div className="flex flex-col items-center gap-1">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-4xl font-normal leading-none tracking-tight tabular-nums text-foreground">
                  {enterprisePriceLabel}
                </span>
                {!isCustomPrice && (
                  <Text size="xs" color="muted">
                    / month
                  </Text>
                )}
              </div>
              {enterpriseCard.priceCaption ? (
                <Text size="xs" color="muted">
                  {enterpriseCard.priceCaption}
                </Text>
              ) : null}
            </div>

            <BillingLockedTooltip className="w-full">
              <Button
                disabled
                data-testid="trial-expired-upgrade-pro-cta"
                className="w-full bg-[hsl(var(--platform-card))] text-[hsl(var(--platform-card-foreground))] hover:bg-[hsl(var(--platform-card))]/90"
                variant="default"
              >
                Upgrade to Enterprise
              </Button>
            </BillingLockedTooltip>
          </div>

          {/* Footer links */}
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={handleViewPlans}
              data-testid="trial-expired-view-plans"
              className="text-sm font-medium text-[hsl(var(--cta))] underline-offset-2 hover:underline"
            >
              View all plans →
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
